import { useAuth } from '@/contexts/AuthContext';

/**
 * Allowlist gate for the tenant Konto/Payday accounting-integration UI.
 *
 * Reads `integrations_enabled` from the backend `/auth/me` payload via
 * AuthContext. That flag is computed on the server from
 * `REKTRA_BETA_ALLOWLIST` env var (see backend/feature_flags.py) so a
 * single source of truth gates both the API endpoints (via the backend
 * `gate_integration` dependency) AND the UI (via this hook).
 *
 * Historical drift (pre-2026-02-25): the hook used to carry its own
 * hardcoded email + kennitala allowlist parallel to the server-side
 * one. When someone was added to the env var but not the hardcoded
 * list (or vice versa) the UI and API disagreed. Fixed by making the
 * server the single source of truth and letting this hook read it.
 *
 * Superadmins bypass the flag — they need integration UI for support
 * triage regardless of the beta gate. The backend already grants
 * superadmins on `flags.integrations_enabled: true` when they call
 * `/auth/me`, so no client-side special-case is required, but we keep
 * an explicit belt-and-braces check anyway.
 *
 * Returns:
 *   { allowed: bool, loading: bool }
 *
 * `loading` stays `false` — no network call is needed anymore since
 * the flag arrives with the initial `/auth/me` fetch that AuthContext
 * already handles. Kept in the return shape for API stability with
 * the 8 consumer sites.
 */
export const useIntegrationsAccess = () => {
  const { user, integrationsEnabled } = useAuth();
  const isSuperadmin = user?.role === 'superadmin' || user?.is_superadmin === true;
  const allowed = !!integrationsEnabled || isSuperadmin;
  return { allowed, loading: false };
};

export default useIntegrationsAccess;
